import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  forkJoin,
  Observable,
  of,
  Subscriber,
  Subscription,
  timer,
} from "rxjs";
import {
  UserSettingRequestData,
  UserSettingJsonData,
  UserSettingPeriodCalculationRequestData,
  ValuationGridData,
  VsPeerState,
} from "../valuation-page/valuation-page.model";
import {
  CHARTS_TAB,
  COMPANY_TAB,
  VS_PEER_TAB,
} from "../valuation-page/valuation-page.constant";
import {
  MONTH_DATE_ABB_YEAR_FORMAT,
  MARKET_DATA_LIST,
} from "src/app/common-constants";
import { Dictionary } from "../store/models/common_types";
import {
  computeValuationGridAverage,
  computeValuationGridMedian,
  createDataCallUrl,
  formatCell,
  computeValuationGridAverageForMcapAndEv,
  computeValuationGridMedianForMcapAndEv,
} from "./valuation-page-utils";
import { formatMCapAndEVCell } from "src/app/utils/common";
import dayjs from "dayjs";
import { getIsAbsolutePeriod } from "@va/core-library/period-filter";
import { convertRelativePeriodLabelToId } from "src/app/utils/period-utils";
@Injectable({
  providedIn: "root",
})
export class ValuationPageDataService {
  apiEP: string = window.apiUrl;
  gridData: ValuationGridData = {};
  dataSubscriber: Subscription;
  companyDataSubscriber: Subscription;
  constructor(private http: HttpClient) {}

  getValuationPageMeta(ticker: string, tab: string): Observable<any> {
    return this.http.get(
      `${this.apiEP}/valuation_page/get_valuation_meta?ticker=${ticker}&tab=${tab}`,
    );
  }

  saveUserSettings(userSettingDetails: any): Observable<any> {
    const jsonData: UserSettingJsonData = {
      ratios: {
        names: userSettingDetails.selectedRatios,
        denom: userSettingDetails.isDenomSelected,
        denom_yoy: userSettingDetails.isDenomYoySelected,
      },
      period: {
        type: userSettingDetails.type,
        basis: userSettingDetails.basis,
        from: userSettingDetails.from,
        to: userSettingDetails.to,
        other: userSettingDetails.other,
        period_type_basis_from: userSettingDetails.period_type_basis_from,
        period_type_basis_to: userSettingDetails.period_type_basis_to,
        is_annual_selected: userSettingDetails.is_annual_selected,
      },
      currency: userSettingDetails.currency,
      peer_ticker: userSettingDetails.tickers,
      revision: userSettingDetails.revision,
      scale_type: userSettingDetails.scaleType,
      orientation: userSettingDetails.orientation,
    };

    const currentTabPropName =
      userSettingDetails.tabType === COMPANY_TAB.id
        ? COMPANY_TAB.mapping
        : userSettingDetails.tabType === CHARTS_TAB.id
          ? CHARTS_TAB.mapping
          : VS_PEER_TAB.mapping;

    const reqObj: UserSettingRequestData = {
      company: userSettingDetails.currentTicker,
      jsonData: {
        [currentTabPropName]: jsonData,
      },
      cut: null,
    };
    return this.http.post(
      `${this.apiEP}/valuation_page/save_company_valuation_settings`,
      reqObj,
    );
  }

  saveUserPeriodCalculationSettings(userPeriodCalDetails): Observable<any> {
    const reqObj: UserSettingPeriodCalculationRequestData = {
      jsonData: {
        ntm_stm_method: userPeriodCalDetails.ntm_stm_method,
        calendarization_method: userPeriodCalDetails.calendarization_method,
      },
    };
    return this.http.post(
      `${this.apiEP}/valuation_page/save_user_period_calculation_settings`,
      reqObj,
    );
  }

  getValuationHeaderData(ticker: string, callForNAV?): Observable<any> {
    if (callForNAV) {
      return this.http.get(
        `${this.apiEP}/nav_page/get_nav_header_data?ticker=${ticker}`,
      );
    }
    return this.http.get(
      `${this.apiEP}/valuation_page/get_valaution_header_data?ticker=${ticker}`,
    );
  }

  getValuationData(
    ratios: Array<string>,
    gridData: ValuationGridData,
    state,
    tab: string,
  ): Observable<ValuationGridData> {
    if (tab === "peer") {
      const optimizedUrl = this.optimizeDataCall(this.gridData, state, ratios);
      if (optimizedUrl[0] !== "") {
        if (optimizedUrl[0] === "delete") {
          this.gridData = this.addDataToGrid({}, gridData, state, true);
          return of(this.gridData);
        }

        const dataCalls: Array<Observable<ValuationGridData>> = [];
        for (let i = 0; i < optimizedUrl.length; i++) {
          const requestUrl = `${this.apiEP}/valuation_page/get_new_valuation_data?${optimizedUrl[i]}`;
          dataCalls.push(this.http.get(requestUrl));
        }
        if (this.dataSubscriber) {
          this.dataSubscriber.unsubscribe();
        }
        return new Observable((observer) => {
          let _data: any = {};
          this.dataSubscriber = forkJoin(dataCalls).subscribe((responses) => {
            for (let i = 0; i < responses.length; i++) {
              const _resp: any = responses[i];
              if (_resp.status === "success") {
                _data = {
                  ..._data,
                  ..._resp.data,
                };
              } else {
                console.error("Error while fetching valuation data");
              }
            }
            this.gridData = this.addDataToGrid(_data, gridData, state, true);
            observer.next(this.gridData);
            observer.complete();
          });
        });
      }
    }

    const urls = createDataCallUrl(ratios, state);
    const dataCalls: Array<Observable<ValuationGridData>> = [];

    for (let i = 0; i < urls.length; i++) {
      const requestUrl = `${this.apiEP}/valuation_page/get_new_valuation_data?${urls[i]}`;
      dataCalls.push(this.http.get(requestUrl));
    }
    return new Observable((observer) => {
      if (tab === "peer") {
        if (this.dataSubscriber) {
          this.dataSubscriber.unsubscribe();
        }
        this.dataSubscriber = this.makeDataCall(
          observer,
          dataCalls,
          tab,
          gridData,
          state,
        );
      } else {
        if (this.companyDataSubscriber) {
          this.companyDataSubscriber.unsubscribe();
        }
        this.companyDataSubscriber = this.makeDataCall(
          observer,
          dataCalls,
          tab,
          gridData,
          state,
        );
      }
    });
  }

  createDelayUsingTimer(delayValue: number) {
    return timer(delayValue);
  }

  getValuationDataCall(url: string) {
    return this.http.get(
      `${this.apiEP}/valuation_page/get_new_valuation_data?${url}`,
    );
  }

  private makeDataCall(
    observer: Subscriber<ValuationGridData>,
    dataCalls: Array<Observable<ValuationGridData>>,
    tab: string,
    gridData: ValuationGridData,
    state,
  ): Subscription {
    let _data: any = {};
    return forkJoin(dataCalls).subscribe((responses) => {
      for (let i = 0; i < responses.length; i++) {
        const _resp: any = responses[i];
        if (_resp.status === "success") {
          _data = {
            ..._data,
            ..._resp.data,
          };
        } else {
          console.error("Error while fetching valuation data");
        }
      }
      if (tab === "peer") {
        this.gridData = this.addDataToGrid(_data, gridData, state, false);
        observer.next(this.gridData);
      } else {
        const _companyGridData = this.addDataToGrid(
          _data,
          gridData,
          state,
          false,
        );
        observer.next(_companyGridData);
      }
      observer.complete();
    });
  }

  optimizeDataCall(
    oldGridData: ValuationGridData,
    newState: VsPeerState,
    ratios: Array<string>,
  ): Array<string> {
    if (!oldGridData.rowData) {
      return [""];
    }
    const oldTickerSet = new Set(oldGridData.rowData.map((_r) => _r.ticker));
    const newTickerSet = new Set(newState.ticker_info.map((_t) => _t.ticker));

    for (let i = 0; i < newState.ticker_info.length; i++) {
      const _t = newState.ticker_info[i];

      if (newTickerSet.has(_t.ticker) && oldTickerSet.has(_t.ticker)) {
        newTickerSet.delete(_t.ticker);
        oldTickerSet.delete(_t.ticker);
      }
    }

    if (newTickerSet.size === 0 && oldTickerSet.size === 0) {
      return [""];
    }
    //ticker change

    //new tickers added
    if (newTickerSet.size > 0) {
      const tickersToAdd = newState.ticker_info.filter((_t) => {
        return newTickerSet.has(_t.ticker);
      });
      const urls = createDataCallUrl(ratios, newState, tickersToAdd);
      return urls;
    }
    //tickers removed
    return ["delete"];
  }

  getTickerDataFromCache(
    ticker: string,
    ratio: string,
    period: string,
  ): Dictionary<any> {
    const _ticker = this.gridData.rowData.filter((_r) => {
      return _r.ticker === ticker;
    });
    if (_ticker.length === 0 || !_ticker[0][ratio]) {
      return {
        raw: undefined,
        formatted: "-",
        currency: "",
        currencySymbol: "",
        rd: "",
      };
    }
    return {
      raw: _ticker[0][ratio][period]["raw"],
      formatted: _ticker[0][ratio][period]["formatted"],
      currency: _ticker[0][ratio][period]["currency"],
      currencySymbol: _ticker[0][ratio][period]["currencySymbol"],
      rd: _ticker[0][ratio][period]["rd"],
      primarySli: _ticker[0][ratio][period]["primarySli"],
    };
  }

  getStdLineItems(
    tickers,
    showSuggestedLines = true,
    showValRatio = true,
    rationSuggestedLinesCount = 10,
    hideCount = false,
  ): Observable<any> {
    const postData = {
      tickers,
      show_suggested_lines: showSuggestedLines,
      show_val_ratio: showValRatio,
      valuation_ratio_suggested_lines: rationSuggestedLinesCount,
      hide_count: hideCount,
    };
    return this.http.post(`${this.apiEP}/comps/get_std_line_items`, postData);
  }

  getTickerExtraColumnDataFromCache(
    ticker: string,
    columnKey: string,
  ): Dictionary<any> {
    const _ticker = this.gridData.rowData.filter((_r) => {
      return _r.ticker === ticker;
    });

    if (_ticker.length === 0 || !_ticker[0][columnKey]) {
      return {
        raw: undefined,
        formatted: "-",
        currency: "",
        currencySymbol: "",
        rd: "",
      };
    }
    return {
      raw: _ticker[0][columnKey]["raw"],
      formatted: _ticker[0][columnKey]["formatted"],
      currency: _ticker[0][columnKey]["currency"],
      currencySymbol: _ticker[0][columnKey]["currencySymbol"],
      rd: _ticker[0][columnKey]["rd"],
      isForecast: _ticker[0][columnKey]["isForecast"],
      lastDate: _ticker[0][columnKey]["lastDate"],
      exchangeFullName: _ticker[0][columnKey]["exchangeFullName"],
    };
  }

  addDataToGrid(
    data: Dictionary<any>,
    gridData: ValuationGridData,
    state,
    optimize = false,
  ): ValuationGridData {
    const _filledGridData = { ...gridData };
    const currency = state.currency;
    const isRelativePeriodType = !getIsAbsolutePeriod(state.period.type);
    for (let i = 0; i < _filledGridData.rowData.length; i++) {
      const _row = _filledGridData.rowData[i];
      if (optimize && !data[_row.ticker]) {
        const cachedLastPriceData = this.getTickerExtraColumnDataFromCache(
          _row.ticker,
          "lastPrice",
        );
        const cachedMarketCapData = this.getTickerExtraColumnDataFromCache(
          _row.ticker,
          "marketCap",
        );
        const cachedEVData = this.getTickerExtraColumnDataFromCache(
          _row.ticker,
          "ev",
        );
        _row.lastPrice = {
          raw: cachedLastPriceData["raw"],
          formatted: cachedLastPriceData["formatted"],
          currency: cachedLastPriceData["currency"],
          currencySymbol: cachedLastPriceData["currencySymbol"],
          rd: cachedLastPriceData["rd"],
          isForecast: cachedLastPriceData["isForecast"],
          lastDate: cachedLastPriceData["lastDate"],
          exchangeFullName: cachedLastPriceData["exchangeFullName"],
        };
        _row.marketCap = {
          raw: cachedMarketCapData["raw"],
          formatted: cachedMarketCapData["formatted"],
          currency: cachedMarketCapData["currency"],
          currencySymbol: cachedMarketCapData["currencySymbol"],
          rd: cachedMarketCapData["rd"],
          isForecast: cachedMarketCapData["isForecast"],
          lastDate: cachedMarketCapData["lastDate"],
          exchangeFullName: cachedMarketCapData["exchangeFullName"],
        };
        _row.ev = {
          raw: cachedEVData["raw"],
          formatted: cachedEVData["formatted"],
          currency: cachedEVData["currency"],
          currencySymbol: cachedEVData["currencySymbol"],
          rd: cachedEVData["rd"],
          isForecast: cachedEVData["isForecast"],
          lastDate: cachedEVData["lastDate"],
          exchangeFullName: cachedEVData["exchangeFullName"],
        };
      } else {
        _row.lastPrice = {
          raw:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["cal_v"]
              ? data[_row.ticker]["prev_close"]["cal_v"]
              : undefined,
          formatted:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["v"]
              ? data[_row.ticker]["prev_close"]["v"]
              : "-",
          currency: undefined,
          currencySymbol: undefined,
          rd:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["rid"]
              ? data[_row.ticker]["prev_close"]["rid"]
              : undefined,
          isForecast:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["is_forecast"]
              ? data[_row.ticker]["prev_close"]["is_forecast"]
              : undefined,
          lastDate:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["last_date"]
              ? dayjs(data[_row.ticker]["prev_close"]["last_date"]).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined,
          exchangeFullName:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["exchange_full_name"]
              ? data[_row.ticker]["prev_close"]["exchange_full_name"]
              : undefined,
        };
        _row.marketCap = {
          raw:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["cal_v"]
              ? data[_row.ticker]["mcap"]["cal_v"]
              : undefined,
          formatted:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["cal_v"]
              ? formatMCapAndEVCell(
                  data[_row.ticker]["mcap"]["cal_v"],
                  data[_row.ticker]["mcap"]["C"] || "",
                )
              : "-",
          currency: currency,
          currencySymbol:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["C"]
              ? data[_row.ticker]["mcap"]["C"]
              : undefined,
          rd:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["rid"]
              ? data[_row.ticker]["mcap"]["rid"]
              : undefined,
          isForecast:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["is_forecast"]
              ? data[_row.ticker]["mcap"]["is_forecast"]
              : undefined,
          lastDate:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["last_date"]
              ? dayjs(data[_row.ticker]["mcap"]["last_date"]).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined,
          exchangeFullName:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["exchange_full_name"]
              ? data[_row.ticker]["mcap"]["exchange_full_name"]
              : undefined,
        };
        _row.ev = {
          raw:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["cal_v"]
              ? data[_row.ticker]["ev"]["cal_v"]
              : undefined,
          formatted:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["cal_v"]
              ? formatMCapAndEVCell(
                  data[_row.ticker]["ev"]["cal_v"],
                  data[_row.ticker]["ev"]["C"] || "",
                )
              : "-",
          currency: currency,
          currencySymbol:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["C"]
              ? data[_row.ticker]["ev"]["C"]
              : undefined,
          rd:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["rid"]
              ? data[_row.ticker]["ev"]["rid"]
              : undefined,
          isForecast:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["is_forecast"]
              ? data[_row.ticker]["ev"]["is_forecast"]
              : undefined,
          lastDate:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["last_date"]
              ? dayjs(data[_row.ticker]["ev"]["last_date"]).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined,
          exchangeFullName:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["exchange_full_name"]
              ? data[_row.ticker]["ev"]["exchange_full_name"]
              : undefined,
        };
      }
      for (let j = 0; j < gridData.ratios.length; j++) {
        const _ratio = gridData.ratios[j];
        _filledGridData.ratioData[_ratio]["currency"] = currency;
        let _dataRatio = _ratio;
        if (_ratio.charAt(0) === "#") {
          _dataRatio = _ratio.substring(1, _ratio.indexOf("_"));
        }
        for (let k = 0; k < gridData.periods.length; k++) {
          const _p = gridData.periods[k];

          let periodKeyUsedInDataCall = _p;
          if (isRelativePeriodType) {
            periodKeyUsedInDataCall = convertRelativePeriodLabelToId(_p);
          }

          if (data[_row.ticker] && data[_row.ticker][_dataRatio]) {
            _filledGridData.ratioData[_ratio]["unit"] =
              data[_row.ticker] && data[_row.ticker][`${_dataRatio}|U`];
            _filledGridData.ratioData[_ratio]["magnitude"] =
              data[_row.ticker] && data[_row.ticker][`${_dataRatio}|M`];
            _filledGridData.ratioData[_ratio]["currencySymbol"] =
              data[_row.ticker] && data[_row.ticker][`${_dataRatio}|C`];
            if (
              data[_row.ticker][_dataRatio][periodKeyUsedInDataCall] &&
              data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                "values"
              ] !== undefined
            ) {
              const valueType = _ratio.includes("denom_yoy") ? "yoy" : "values";
              _row[_ratio][_p]["raw"] =
                data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                  valueType
                ];
              _row[_ratio][_p]["formatted"] = formatCell(
                data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                  valueType
                ],
                _filledGridData.ratioData[_ratio]["currencySymbol"],
                _filledGridData.ratioData[_ratio]["unit"],
                valueType,
              );
              _row[_ratio][_p]["currency"] = currency;
              _row[_ratio][_p]["currencySymbol"] =
                _filledGridData.ratioData[_ratio]["currencySymbol"];
              _row[_ratio][_p]["rd"] =
                data[_row.ticker][`${_dataRatio}|RD`][periodKeyUsedInDataCall][
                  valueType
                ];
              _row[_ratio][_p]["isForecast"] =
                data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                  "is_forecast"
                ];
              _row[_ratio][_p]["primarySli"] =
                data[_row.ticker][`${_dataRatio}|primarysli`];
            } else {
              if (optimize) {
                const cachedData = this.getTickerDataFromCache(
                  _row.ticker,
                  _ratio,
                  _p,
                );
                _row[_ratio][_p]["raw"] = cachedData["raw"];
                _row[_ratio][_p]["formatted"] = cachedData["formatted"];
                _row[_ratio][_p]["currency"] = cachedData["currency"];
                _row[_ratio][_p]["currencySymbol"] =
                  cachedData["currencySymbol"];
                _row[_ratio][_p]["rd"] = cachedData["rd"];
                _row[_ratio][_p]["isForecast"] = cachedData["isForecast"];
                _row[_ratio][_p]["primarySli"] = cachedData["primarySli"];
              } else {
                _row[_ratio][_p]["raw"] = undefined;
                _row[_ratio][_p]["formatted"] = "-";
                _row[_ratio][_p]["currency"] = undefined;
                _row[_ratio][_p]["currencySymbol"] =
                  _filledGridData.ratioData[_ratio]["currencySymbol"];
                _row[_ratio][_p]["rd"] = "";
                _row[_ratio][_p]["isForecast"] = undefined;
              }
            }
          } else {
            if (optimize) {
              const cachedData = this.getTickerDataFromCache(
                _row.ticker,
                _ratio,
                _p,
              );
              _row[_ratio][_p]["raw"] = cachedData["raw"];
              _row[_ratio][_p]["formatted"] = cachedData["formatted"];
              _row[_ratio][_p]["currency"] = cachedData["currency"];
              _row[_ratio][_p]["currencySymbol"] = cachedData["currencySymbol"];
              _row[_ratio][_p]["rd"] = cachedData["rd"];
              _row[_ratio][_p]["isForecast"] = cachedData["isForecast"];
              _row[_ratio][_p]["primarySli"] = cachedData["primarySli"];
            } else {
              _row[_ratio][_p]["raw"] = undefined;
              _row[_ratio][_p]["formatted"] = "-";
              _row[_ratio][_p]["currency"] = undefined;
              _row[_ratio][_p]["currencySymbol"] = undefined;
              _row[_ratio][_p]["rd"] = "";
              _row[_ratio][_p]["isForecast"] = undefined;
            }
          }
        }
      }
      _filledGridData.rowData[i] = _row;
    }
    _filledGridData.footerData.average = computeValuationGridAverage(
      gridData,
      currency,
    );
    _filledGridData.footerData.median = computeValuationGridMedian(
      gridData,
      currency,
    );
    return _filledGridData;
  }

  formatLastPrice(price, currency) {
    if (price === undefined || price === null) {
      return "-";
    }

    const valueToUse = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 1,
    }).format(Math.abs(price));

    currency = currency || "";
    return `${currency}${valueToUse}`;
  }

  addDataToGridMcapEVLastPrice(
    data: Dictionary<any>,
    gridData: ValuationGridData,
    state,
  ): ValuationGridData {
    const _filledGridData = { ...gridData };
    const currency = state.currency;
    const lastPriceId = MARKET_DATA_LIST[0].id;
    const marketCapId = MARKET_DATA_LIST[1].id;
    const evId = MARKET_DATA_LIST[2].id;
    let tickerBindindCounter = 0;
    for (let i = 0; i < _filledGridData.rowData.length; i++) {
      if (tickerBindindCounter >= Object.keys(data).length) {
        break;
      }
      const _row = _filledGridData.rowData[i];
      if (data[_row.ticker] && data[_row.ticker]["market_data"]) {
        tickerBindindCounter++;
        const marketDataObj = data[_row.ticker]["market_data"];
        // Need to find a way to remove the dependancy of "|data_exists"
        if (lastPriceId in marketDataObj) {
          _row.lastPrice = {
            raw:
              marketDataObj[lastPriceId] && marketDataObj[lastPriceId]["values"]
                ? marketDataObj[lastPriceId]["values"]
                : undefined,
            formatted:
              marketDataObj[lastPriceId] && marketDataObj[lastPriceId]["values"]
                ? this.formatLastPrice(
                    marketDataObj[lastPriceId]["values"],
                    marketDataObj["10|C"] || "",
                  )
                : "-",
            currency: undefined,
            currencySymbol: undefined,
            rd:
              marketDataObj["10|RD"] && marketDataObj["10|RD"]["values"]
                ? marketDataObj["10|RD"]["values"]
                : undefined,
            isForecast:
              data[_row.ticker] &&
              data[_row.ticker]["prev_close"] &&
              data[_row.ticker]["prev_close"]["is_forecast"]
                ? data[_row.ticker]["prev_close"]["is_forecast"]
                : undefined,
            lastDate:
              marketDataObj[lastPriceId] &&
              marketDataObj[lastPriceId]["last_date"]
                ? dayjs(marketDataObj[lastPriceId]["last_date"]).format(
                    MONTH_DATE_ABB_YEAR_FORMAT,
                  )
                : undefined,
            exchangeFullName:
              marketDataObj[lastPriceId] &&
              marketDataObj[lastPriceId]["exchange_full_name"]
                ? marketDataObj[lastPriceId]["exchange_full_name"]
                : undefined,
          };
        } else if (
          "10|data_exists" in marketDataObj &&
          marketDataObj["10|data_exists"] === false
        ) {
          _row.lastPrice = {
            raw: undefined,
            formatted: "-",
            currency: undefined,
            currencySymbol: undefined,
            rd: undefined,
            isForecast: undefined,
            lastDate: undefined,
            exchangeFullName: undefined,
          };
        }

        if (marketCapId in marketDataObj) {
          _row.marketCap = {
            raw:
              marketDataObj[marketCapId] && marketDataObj[marketCapId]["values"]
                ? marketDataObj[marketCapId]["values"]
                : undefined,
            formatted:
              marketDataObj[marketCapId] && marketDataObj[marketCapId]["values"]
                ? formatMCapAndEVCell(
                    marketDataObj[marketCapId]["values"],
                    marketDataObj["11|C"] || "",
                  )
                : "-",
            currency: currency,
            currencySymbol: marketDataObj["11|C"]
              ? marketDataObj["11|C"]
              : undefined,
            rd:
              marketDataObj["11|RD"] && marketDataObj["11|RD"]["values"]
                ? marketDataObj["11|RD"]["values"]
                : undefined,
            isForecast:
              data[_row.ticker] &&
              data[_row.ticker]["mcap"] &&
              data[_row.ticker]["mcap"]["is_forecast"]
                ? data[_row.ticker]["mcap"]["is_forecast"]
                : undefined,
            lastDate:
              marketDataObj[marketCapId] &&
              marketDataObj[marketCapId]["last_date"]
                ? dayjs(marketDataObj[marketCapId]["last_date"]).format(
                    MONTH_DATE_ABB_YEAR_FORMAT,
                  )
                : undefined,
            exchangeFullName:
              marketDataObj[marketCapId] &&
              marketDataObj[marketCapId]["exchange_full_name"]
                ? marketDataObj[marketCapId]["exchange_full_name"]
                : undefined,
          };
        } else if (
          "11|data_exists" in marketDataObj &&
          marketDataObj["11|data_exists"] === false
        ) {
          _row.marketCap = {
            raw: undefined,
            formatted: "-",
            currency: undefined,
            currencySymbol: undefined,
            rd: undefined,
            isForecast: undefined,
            lastDate: undefined,
            exchangeFullName: undefined,
          };
        }

        if (evId in marketDataObj) {
          _row.ev = {
            raw: marketDataObj[evId]["values"]
              ? marketDataObj[evId]["values"]
              : undefined,
            formatted: marketDataObj[evId]["values"]
              ? formatMCapAndEVCell(
                  marketDataObj[evId]["values"],
                  marketDataObj["34|C"] || "",
                )
              : "-",
            currency: currency,
            currencySymbol: marketDataObj["34|C"]
              ? marketDataObj["34|C"]
              : undefined,
            rd:
              marketDataObj["34|RD"] && marketDataObj["34|RD"]["values"]
                ? marketDataObj["34|RD"]["values"]
                : undefined,
            isForecast:
              data[_row.ticker] &&
              data[_row.ticker]["ev"] &&
              data[_row.ticker]["ev"]["is_forecast"]
                ? data[_row.ticker]["ev"]["is_forecast"]
                : undefined,
            lastDate: marketDataObj[evId]["last_date"]
              ? dayjs(marketDataObj[evId]["last_date"]).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined,
            exchangeFullName: marketDataObj[evId]["exchange_full_name"]
              ? marketDataObj[evId]["exchange_full_name"]
              : undefined,
          };
        } else if (
          "34|data_exists" in marketDataObj &&
          marketDataObj["34|data_exists"] === false
        ) {
          _row.ev = {
            raw: undefined,
            formatted: "-",
            currency: undefined,
            currencySymbol: undefined,
            rd: undefined,
            isForecast: undefined,
            lastDate: undefined,
            exchangeFullName: undefined,
          };
        }
        _filledGridData.rowData[i] = _row;
      }
    }
    return _filledGridData;
  }

  addComparativeDataToGridV1(
    data: Dictionary<any>,
    gridData: ValuationGridData,
    state,
  ): ValuationGridData {
    const _filledGridData = { ...gridData };
    const currency = state.currency;
    const isRelativePeriodType = !getIsAbsolutePeriod(state.period.type);
    let tickerBindindCounter = 0;
    for (let i = 0; i < _filledGridData.rowData.length; i++) {
      if (tickerBindindCounter >= Object.keys(data).length) {
        break;
      }
      const _row = _filledGridData.rowData[i];
      if (data[_row.ticker]) {
        tickerBindindCounter++;
        for (let j = 0; j < gridData.ratios.length; j++) {
          const _ratio = gridData.ratios[j];
          _filledGridData.ratioData[_ratio]["currency"] = currency;
          let _dataRatio = _ratio;
          if (_ratio.charAt(0) === "#") {
            _dataRatio = _ratio.substring(1, _ratio.indexOf("_"));
          }
          for (let k = 0; k < gridData.periods.length; k++) {
            const _p = gridData.periods[k];

            let periodKeyUsedInDataCall = _p;
            if (isRelativePeriodType) {
              periodKeyUsedInDataCall = convertRelativePeriodLabelToId(_p);
            }

            if (data[_row.ticker] && data[_row.ticker][_dataRatio]) {
              _filledGridData.ratioData[_ratio]["unit"] =
                data[_row.ticker] && data[_row.ticker][`${_dataRatio}|U`];
              _filledGridData.ratioData[_ratio]["magnitude"] =
                data[_row.ticker] && data[_row.ticker][`${_dataRatio}|M`];
              _filledGridData.ratioData[_ratio]["currencySymbol"] =
                data[_row.ticker] && data[_row.ticker][`${_dataRatio}|C`];
              if (
                data[_row.ticker][_dataRatio][periodKeyUsedInDataCall] &&
                data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                  "values"
                ] !== undefined
              ) {
                const valueType = _ratio.includes("denom_yoy")
                  ? "yoy"
                  : "values";
                _row[_ratio][_p]["raw"] =
                  data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                    valueType
                  ];
                _row[_ratio][_p]["formatted"] = formatCell(
                  data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                    valueType
                  ],
                  _filledGridData.ratioData[_ratio]["currencySymbol"],
                  _filledGridData.ratioData[_ratio]["unit"],
                  valueType,
                );
                _row[_ratio][_p]["currency"] = currency;
                _row[_ratio][_p]["currencySymbol"] =
                  _filledGridData.ratioData[_ratio]["currencySymbol"];
                _row[_ratio][_p]["rd"] =
                  data[_row.ticker][`${_dataRatio}|RD`][
                    periodKeyUsedInDataCall
                  ][valueType];
                _row[_ratio][_p]["isForecast"] =
                  data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                    "is_forecast"
                  ];
                _row[_ratio][_p]["primarySli"] =
                  data[_row.ticker][`${_dataRatio}|primarysli`];
              } else {
                _row[_ratio][_p]["raw"] = undefined;
                _row[_ratio][_p]["formatted"] = "-";
                _row[_ratio][_p]["currency"] = undefined;
                _row[_ratio][_p]["currencySymbol"] =
                  _filledGridData.ratioData[_ratio]["currencySymbol"];
                _row[_ratio][_p]["rd"] = "";
                _row[_ratio][_p]["isForecast"] = undefined;
              }
            } else {
              _row[_ratio][_p]["raw"] = undefined;
              _row[_ratio][_p]["formatted"] = "-";
              _row[_ratio][_p]["currency"] = undefined;
              _row[_ratio][_p]["currencySymbol"] = undefined;
              _row[_ratio][_p]["rd"] = "";
              _row[_ratio][_p]["isForecast"] = undefined;
            }
          }
        }
        _filledGridData.rowData[i] = _row;
      }
    }

    return _filledGridData;
  }

  addDataToGridV1(
    data: Dictionary<any>,
    gridData: ValuationGridData,
    state,
  ): ValuationGridData {
    const _filledGridData = { ...gridData };
    const currency = state.currency;
    const isRelativePeriodType = !getIsAbsolutePeriod(state.period.type);
    let tickerBindindCounter = 0;
    for (let i = 0; i < _filledGridData.rowData.length; i++) {
      if (tickerBindindCounter >= Object.keys(data).length) {
        break;
      }
      const _row = _filledGridData.rowData[i];
      if (data[_row.ticker]) {
        tickerBindindCounter++;
        _row.lastPrice = {
          raw:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["cal_v"]
              ? data[_row.ticker]["prev_close"]["cal_v"]
              : undefined,
          formatted:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["v"]
              ? data[_row.ticker]["prev_close"]["v"]
              : "-",
          currency: undefined,
          currencySymbol: undefined,
          rd:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["rid"]
              ? data[_row.ticker]["prev_close"]["rid"]
              : undefined,
          isForecast:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["is_forecast"]
              ? data[_row.ticker]["prev_close"]["is_forecast"]
              : undefined,
          lastDate:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["last_date"]
              ? dayjs(data[_row.ticker]["prev_close"]["last_date"]).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined,
          exchangeFullName:
            data[_row.ticker] &&
            data[_row.ticker]["prev_close"] &&
            data[_row.ticker]["prev_close"]["exchange_full_name"]
              ? data[_row.ticker]["prev_close"]["exchange_full_name"]
              : undefined,
        };
        _row.marketCap = {
          raw:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["cal_v"]
              ? data[_row.ticker]["mcap"]["cal_v"]
              : undefined,
          formatted:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["cal_v"]
              ? formatMCapAndEVCell(
                  data[_row.ticker]["mcap"]["cal_v"],
                  data[_row.ticker]["mcap"]["C"] || "",
                )
              : "-",
          currency: currency,
          currencySymbol:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["C"]
              ? data[_row.ticker]["mcap"]["C"]
              : undefined,
          rd:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["rid"]
              ? data[_row.ticker]["mcap"]["rid"]
              : undefined,
          isForecast:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["is_forecast"]
              ? data[_row.ticker]["mcap"]["is_forecast"]
              : undefined,
          lastDate:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["last_date"]
              ? dayjs(data[_row.ticker]["mcap"]["last_date"]).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined,
          exchangeFullName:
            data[_row.ticker] &&
            data[_row.ticker]["mcap"] &&
            data[_row.ticker]["mcap"]["exchange_full_name"]
              ? data[_row.ticker]["mcap"]["exchange_full_name"]
              : undefined,
        };
        _row.ev = {
          raw:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["cal_v"]
              ? data[_row.ticker]["ev"]["cal_v"]
              : undefined,
          formatted:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["cal_v"]
              ? formatMCapAndEVCell(
                  data[_row.ticker]["ev"]["cal_v"],
                  data[_row.ticker]["ev"]["C"] || "",
                )
              : "-",
          currency: currency,
          currencySymbol:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["C"]
              ? data[_row.ticker]["ev"]["C"]
              : undefined,
          rd:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["rid"]
              ? data[_row.ticker]["ev"]["rid"]
              : undefined,
          isForecast:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["is_forecast"]
              ? data[_row.ticker]["ev"]["is_forecast"]
              : undefined,
          lastDate:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["last_date"]
              ? dayjs(data[_row.ticker]["ev"]["last_date"]).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined,
          exchangeFullName:
            data[_row.ticker] &&
            data[_row.ticker]["ev"] &&
            data[_row.ticker]["ev"]["exchange_full_name"]
              ? data[_row.ticker]["ev"]["exchange_full_name"]
              : undefined,
        };
        for (let j = 0; j < gridData.ratios.length; j++) {
          const _ratio = gridData.ratios[j];
          _filledGridData.ratioData[_ratio]["currency"] = currency;
          let _dataRatio = _ratio;
          if (_ratio.charAt(0) === "#") {
            _dataRatio = _ratio.substring(1, _ratio.indexOf("_"));
          }
          for (let k = 0; k < gridData.periods.length; k++) {
            const _p = gridData.periods[k];

            let periodKeyUsedInDataCall = _p;
            if (isRelativePeriodType) {
              periodKeyUsedInDataCall = convertRelativePeriodLabelToId(_p);
            }

            if (data[_row.ticker] && data[_row.ticker][_dataRatio]) {
              _filledGridData.ratioData[_ratio]["unit"] =
                data[_row.ticker] && data[_row.ticker][`${_dataRatio}|U`];
              _filledGridData.ratioData[_ratio]["magnitude"] =
                data[_row.ticker] && data[_row.ticker][`${_dataRatio}|M`];
              _filledGridData.ratioData[_ratio]["currencySymbol"] =
                data[_row.ticker] && data[_row.ticker][`${_dataRatio}|C`];
              if (
                data[_row.ticker][_dataRatio][periodKeyUsedInDataCall] &&
                data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                  "values"
                ] !== undefined
              ) {
                const valueType = _ratio.includes("denom_yoy")
                  ? "yoy"
                  : "values";
                _row[_ratio][_p]["raw"] =
                  data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                    valueType
                  ];
                _row[_ratio][_p]["formatted"] = formatCell(
                  data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                    valueType
                  ],
                  _filledGridData.ratioData[_ratio]["currencySymbol"],
                  _filledGridData.ratioData[_ratio]["unit"],
                  valueType,
                );
                _row[_ratio][_p]["currency"] = currency;
                _row[_ratio][_p]["currencySymbol"] =
                  _filledGridData.ratioData[_ratio]["currencySymbol"];
                _row[_ratio][_p]["rd"] =
                  data[_row.ticker][`${_dataRatio}|RD`][
                    periodKeyUsedInDataCall
                  ][valueType];
                _row[_ratio][_p]["isForecast"] =
                  data[_row.ticker][_dataRatio][periodKeyUsedInDataCall][
                    "is_forecast"
                  ];
                _row[_ratio][_p]["primarySli"] =
                  data[_row.ticker][`${_dataRatio}|primarysli`];
              } else {
                _row[_ratio][_p]["raw"] = undefined;
                _row[_ratio][_p]["formatted"] = "-";
                _row[_ratio][_p]["currency"] = undefined;
                _row[_ratio][_p]["currencySymbol"] =
                  _filledGridData.ratioData[_ratio]["currencySymbol"];
                _row[_ratio][_p]["rd"] = "";
                _row[_ratio][_p]["isForecast"] = undefined;
              }
            } else {
              _row[_ratio][_p]["raw"] = undefined;
              _row[_ratio][_p]["formatted"] = "-";
              _row[_ratio][_p]["currency"] = undefined;
              _row[_ratio][_p]["currencySymbol"] = undefined;
              _row[_ratio][_p]["rd"] = "";
              _row[_ratio][_p]["isForecast"] = undefined;
            }
          }
        }
        _filledGridData.rowData[i] = _row;
      }
    }

    return _filledGridData;
  }

  calculateFooterData(
    gridData,
    currency,
    calculateFooterDataForMCapAndEV = true,
  ) {
    const _filledGridData = { ...gridData };
    _filledGridData.footerData.average = {
      ..._filledGridData.footerData.average,
      ...computeValuationGridAverage(
        gridData,
        currency,
        calculateFooterDataForMCapAndEV,
      ),
    };
    _filledGridData.footerData.median = {
      ..._filledGridData.footerData.median,
      ...computeValuationGridMedian(
        gridData,
        currency,
        calculateFooterDataForMCapAndEV,
      ),
    };
    return _filledGridData;
  }

  calculateFooterDataForMcapEvLp(gridData, currency) {
    const _filledGridData = { ...gridData };
    _filledGridData.footerData.average = {
      ..._filledGridData.footerData.average,
      ...computeValuationGridAverageForMcapAndEv(gridData, currency),
    };
    _filledGridData.footerData.median = {
      ..._filledGridData.footerData.median,
      ...computeValuationGridMedianForMcapAndEv(gridData, currency),
    };
    return _filledGridData;
  }

  addDataToCompanyGrid(
    data: Dictionary<any>,
    gridData: ValuationGridData,
    currency: string,
  ): ValuationGridData {
    const _filledGridData = { ...gridData };
    for (let i = 0; i < _filledGridData.rowData.length; i++) {
      const _row = _filledGridData.rowData[i];
      _row.lastPrice = {
        raw:
          data[_row.ticker] &&
          data[_row.ticker]["prev_close"] &&
          data[_row.ticker]["prev_close"]["cal_v"]
            ? data[_row.ticker]["prev_close"]["cal_v"]
            : undefined,
        formatted:
          data[_row.ticker] &&
          data[_row.ticker]["prev_close"] &&
          data[_row.ticker]["prev_close"]["v"]
            ? data[_row.ticker]["prev_close"]["v"]
            : "-",
        currency: undefined,
        currencySymbol: undefined,
        rd:
          data[_row.ticker] &&
          data[_row.ticker]["prev_close"] &&
          data[_row.ticker]["prev_close"]["rid"]
            ? data[_row.ticker]["prev_close"]["rid"]
            : undefined,
        isForecast:
          data[_row.ticker] &&
          data[_row.ticker]["prev_close"] &&
          data[_row.ticker]["prev_close"]["is_forecast"]
            ? data[_row.ticker]["prev_close"]["is_forecast"]
            : undefined,
        lastDate:
          data[_row.ticker] &&
          data[_row.ticker]["prev_close"] &&
          data[_row.ticker]["prev_close"]["last_date"]
            ? dayjs(data[_row.ticker]["prev_close"]["last_date"]).format(
                MONTH_DATE_ABB_YEAR_FORMAT,
              )
            : undefined,
        exchangeFullName:
          data[_row.ticker] &&
          data[_row.ticker]["prev_close"] &&
          data[_row.ticker]["prev_close"]["exchange_full_name"]
            ? data[_row.ticker]["prev_close"]["exchange_full_name"]
            : undefined,
      };
      _row.marketCap = {
        raw:
          data[_row.ticker] &&
          data[_row.ticker]["mcap"] &&
          data[_row.ticker]["mcap"]["cal_v"]
            ? data[_row.ticker]["mcap"]["cal_v"]
            : undefined,
        formatted:
          data[_row.ticker] &&
          data[_row.ticker]["mcap"] &&
          data[_row.ticker]["mcap"]["cal_v"]
            ? formatMCapAndEVCell(
                data[_row.ticker]["mcap"]["cal_v"],
                data[_row.ticker]["mcap"]["C"] || "",
              )
            : "-",
        currency: currency,
        currencySymbol:
          data[_row.ticker] &&
          data[_row.ticker]["mcap"] &&
          data[_row.ticker]["mcap"]["C"]
            ? data[_row.ticker]["mcap"]["C"]
            : undefined,
        rd:
          data[_row.ticker] &&
          data[_row.ticker]["mcap"] &&
          data[_row.ticker]["mcap"]["rid"]
            ? data[_row.ticker]["mcap"]["rid"]
            : undefined,
        isForecast:
          data[_row.ticker] &&
          data[_row.ticker]["mcap"] &&
          data[_row.ticker]["mcap"]["is_forecast"]
            ? data[_row.ticker]["mcap"]["is_forecast"]
            : undefined,
        lastDate:
          data[_row.ticker] &&
          data[_row.ticker]["mcap"] &&
          data[_row.ticker]["mcap"]["last_date"]
            ? dayjs(data[_row.ticker]["mcap"]["last_date"]).format(
                MONTH_DATE_ABB_YEAR_FORMAT,
              )
            : undefined,
        exchangeFullName:
          data[_row.ticker] &&
          data[_row.ticker]["mcap"] &&
          data[_row.ticker]["mcap"]["exchange_full_name"]
            ? data[_row.ticker]["mcap"]["exchange_full_name"]
            : undefined,
      };
      _row.ev = {
        raw:
          data[_row.ticker] &&
          data[_row.ticker]["ev"] &&
          data[_row.ticker]["ev"]["cal_v"]
            ? data[_row.ticker]["ev"]["cal_v"]
            : undefined,
        formatted:
          data[_row.ticker] &&
          data[_row.ticker]["ev"] &&
          data[_row.ticker]["ev"]["cal_v"]
            ? formatMCapAndEVCell(
                data[_row.ticker]["ev"]["cal_v"],
                data[_row.ticker]["ev"]["C"] || "",
              )
            : "-",
        currency: currency,
        currencySymbol:
          data[_row.ticker] &&
          data[_row.ticker]["ev"] &&
          data[_row.ticker]["ev"]["C"]
            ? data[_row.ticker]["ev"]["C"]
            : undefined,
        rd:
          data[_row.ticker] &&
          data[_row.ticker]["ev"] &&
          data[_row.ticker]["ev"]["rid"]
            ? data[_row.ticker]["ev"]["rid"]
            : undefined,
        isForecast:
          data[_row.ticker] &&
          data[_row.ticker]["ev"] &&
          data[_row.ticker]["ev"]["is_forecast"]
            ? data[_row.ticker]["ev"]["is_forecast"]
            : undefined,
        lastDate:
          data[_row.ticker] &&
          data[_row.ticker]["ev"] &&
          data[_row.ticker]["ev"]["last_date"]
            ? dayjs(data[_row.ticker]["ev"]["last_date"]).format(
                MONTH_DATE_ABB_YEAR_FORMAT,
              )
            : undefined,
        exchangeFullName:
          data[_row.ticker] &&
          data[_row.ticker]["ev"] &&
          data[_row.ticker]["ev"]["exchange_full_name"]
            ? data[_row.ticker]["ev"]["exchange_full_name"]
            : undefined,
      };
      for (let j = 0; j < gridData.ratios.length; j++) {
        const _ratio = gridData.ratios[j];
        _filledGridData.ratioData[_ratio]["currency"] = currency;
        let _dataRatio = _ratio;
        if (_ratio.charAt(0) === "#") {
          _dataRatio = _ratio.substring(1, _ratio.indexOf("_"));
        }
        for (let k = 0; k < gridData.periods.length; k++) {
          const _p = gridData.periods[k];
          if (data[_row.ticker] && data[_row.ticker][_dataRatio]) {
            _filledGridData.ratioData[_ratio]["unit"] =
              data[_row.ticker] && data[_row.ticker][`${_dataRatio}|U`];
            _filledGridData.ratioData[_ratio]["magnitude"] =
              data[_row.ticker] && data[_row.ticker][`${_dataRatio}|M`];
            _filledGridData.ratioData[_ratio]["currencySymbol"] =
              data[_row.ticker] && data[_row.ticker][`${_dataRatio}|C`];
            if (data[_row.ticker][_dataRatio][_p]) {
              if (data[_row.ticker][_dataRatio][_p]["values"] !== undefined) {
                const valueType = _ratio.includes("denom_yoy")
                  ? "yoy"
                  : "values";
                _row[_ratio][_p]["raw"] =
                  data[_row.ticker][_dataRatio][_p][valueType];
                _row[_ratio][_p]["formatted"] = formatCell(
                  data[_row.ticker][_dataRatio][_p][valueType],
                  _filledGridData.ratioData[_ratio]["currencySymbol"],
                  _filledGridData.ratioData[_ratio]["unit"],
                  valueType,
                );
                _row[_ratio][_p]["currency"] = currency;
                _row[_ratio][_p]["currencySymbol"] =
                  data[_row.ticker][`${_dataRatio}|C`];
                _row[_ratio][_p]["rd"] =
                  data[_row.ticker][`${_dataRatio}|RD`][_p][valueType];
                _row[_ratio][_p]["isForecast"] =
                  data[_row.ticker][_dataRatio][_p]["is_forecast"];
                _row[_ratio][_p]["primarySli"] =
                  data[_row.ticker][`${_dataRatio}|primarysli`];
              } else {
                _row[_ratio][_p]["raw"] = undefined;
                _row[_ratio][_p]["formatted"] = "-";
                _row[_ratio][_p]["currency"] = currency;
                _row[_ratio][_p]["currencySymbol"] =
                  _filledGridData.ratioData[_ratio]["currencySymbol"];
                _row[_ratio][_p]["rd"] = "";
                _row[_ratio][_p]["isForecast"] = undefined;
              }
            }
          } else {
            _row[_ratio][_p]["raw"] = undefined;
            _row[_ratio][_p]["formatted"] = "-";
            _row[_ratio][_p]["currency"] = currency;
            _row[_ratio][_p]["currencySymbol"] =
              _filledGridData.ratioData[_ratio]["currencySymbol"];
            _row[_ratio][_p]["rd"] = "";
            _row[_ratio][_p]["isForecast"] = undefined;
          }
        }
      }
      _filledGridData.rowData[i] = _row;
    }
    _filledGridData.footerData.average = computeValuationGridAverage(
      gridData,
      currency,
    );
    _filledGridData.footerData.median = computeValuationGridMedian(
      gridData,
      currency,
    );

    return _filledGridData;
  }

  destroyValuationGridData() {
    this.gridData = {};
  }
}
